import React, { useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Stack,
  Text
} from '@chakra-ui/react'
import { format, startOfMonth, subDays } from 'date-fns'
import startCase from 'lodash/startCase'

import axios from 'lib/axios'

import InfoHint from '../components/InfoHint'
import { ShipStatusResponse, formatDisplayDate, getErrorMessage } from '../types'

type Props = {
  shipStatus: ShipStatusResponse | null
}

const dateToday = (): string => new Date().toISOString().slice(0, 10)
const lastDayOfPreviousMonth = (): string =>
  format(subDays(startOfMonth(new Date()), 1), 'yyyy-MM-dd')

const UpdateConfigurations = ({ shipStatus }: Props) => {
  const [appEndDate, setAppEndDate] = useState(lastDayOfPreviousMonth())
  const [lastShipDate, setLastShipDate] = useState(dateToday())
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const onSave = async () => {
    try {
      setIsLoading(true)
      setError('')
      setSuccess('')

      await axios.post('/admin/statements/configurations', {
        appEndDate,
        lastShipDate
      })

      setSuccess('Statement configurations updated.')
    } catch (err: any) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Text>
        Current {startCase('app_end_date')}:{' '}
        <Text as='span' fontWeight='bold'>
          {formatDisplayDate(shipStatus?.appEndDate)}
        </Text>
        <InfoHint label='Source: statement_configurations.app_end_date in Postgres. Represents the most recently shipped statement month.' />
      </Text>
      <Text>
        Current {startCase('last_ship_date')}:{' '}
        <Text as='span' fontWeight='bold'>
          {formatDisplayDate(shipStatus?.lastShipDate)}
        </Text>
        <InfoHint label='Source: statement_configurations.last_ship_date in Postgres. Represents when statements were last sent.' />
      </Text>

      <FormControl maxW='280px'>
        <FormLabel>
          {startCase('app_end_date')}
          <InfoHint label='Set this to the statement month end you just shipped (for example, Jan statements => 2026-01-31).' />
        </FormLabel>
        <Input
          type='date'
          value={appEndDate}
          onChange={(event) => setAppEndDate(event.target.value)}
        />
      </FormControl>
      <FormControl maxW='280px'>
        <FormLabel>
          {startCase('last_ship_date')}
          <InfoHint label='Set this to the actual date statements were sent. This helps determine snapshot/adjustment context next cycle.' />
        </FormLabel>
        <Input
          type='date'
          value={lastShipDate}
          onChange={(event) => setLastShipDate(event.target.value)}
        />
      </FormControl>
      <HStack spacing={1} alignSelf='flex-start' alignItems='center'>
        <Button
          colorScheme='teal'
          variant='solid'
          onClick={onSave}
          isDisabled={isLoading}
          alignSelf='flex-start'
        >
          Update Configurations
        </Button>
        <InfoHint label='Writes both values to Postgres table statement_configurations. This should be the final step after a successful live send.' />
      </HStack>

      {error && (
        <Alert status='error'>
          <AlertIcon />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert status='success'>
          <AlertIcon />
          <AlertDescription>{success}</AlertDescription>
        </Alert>
      )}
    </Stack>
  )
}

export default UpdateConfigurations
